import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import SEO from '../components/SEO';
import { fetchCollection, fetchBeforeAfterOrder, saveBeforeAfterOrder, getImageUrl, clearCache } from '../lib/cms';
import { getImageRecencyScore } from '../lib/imageSorting';

export default function BeforeAfterReorderPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSavedToast, setShowSavedToast] = useState(false);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const saveTimeoutRef = useRef(null);

  // Initial load
  const loadData = useCallback(async () => {
    setLoading(true);
    setSaveError(null);
    try {
      const [rawItems, savedOrder] = await Promise.all([
        fetchCollection('foer_efter'),
        fetchBeforeAfterOrder()
      ]);

      if (rawItems && rawItems.length > 0) {
        const orderMap = new Map();
        if (Array.isArray(savedOrder) && savedOrder.length > 0) {
          savedOrder.forEach((id, index) => {
            orderMap.set(String(id), index);
          });
        }

        const sorted = [...rawItems].sort((a, b) => {
          const idA = String(a.id || '');
          const idB = String(b.id || '');
          const hasA = orderMap.has(idA);
          const hasB = orderMap.has(idB);

          if (hasA && hasB) {
            return orderMap.get(idA) - orderMap.get(idB);
          }
          if (hasA && !hasB) return -1;
          if (!hasA && hasB) return 1;

          // Fallback to recency score
          const urlA_after = getImageUrl(a.efter_billede || a.after_image, '');
          const urlA_before = getImageUrl(a.foer_billede || a.before_image, '');
          const scoreA = Math.max(getImageRecencyScore(urlA_after), getImageRecencyScore(urlA_before));

          const urlB_after = getImageUrl(b.efter_billede || b.after_image, '');
          const urlB_before = getImageUrl(b.foer_billede || b.before_image, '');
          const scoreB = Math.max(getImageRecencyScore(urlB_after), getImageRecencyScore(urlB_before));

          if (scoreA !== scoreB) return scoreB - scoreA;

          const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return dateB - dateA;
        });

        const formatted = sorted.map((item, idx) => ({
          id: String(item.id || `item-${idx}`),
          title: item.titel || item.title || `Projekt #${idx + 1}`,
          location: item.lokation || item.location || '',
          category: item.kategori || item.category || 'Belægning & Have',
          beforeUrl: getImageUrl(item.foer_billede || item.before_image, ''),
          beforeCaption: item.foer_tekst || item.before_text || '',
          afterUrl: getImageUrl(item.efter_billede || item.after_image, ''),
          afterCaption: item.efter_tekst || item.after_text || '',
          rawItem: item
        }));

        setItems(formatted);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Kunne ikke hente før/efter-data:', err);
      setSaveError('Der opstod en fejl ved indlæsning af projekterne.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Persist order to backend
  const persistOrder = async (newItems) => {
    if (!newItems || newItems.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const orderIds = newItems.map(item => item.id);
      await saveBeforeAfterOrder(orderIds);
      clearCache('foer_efter');
      const now = new Date();
      setLastSaved(now.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setShowSavedToast(true);
      setTimeout(() => setShowSavedToast(false), 3000);
    } catch (err) {
      console.error('Fejl ved lagring af rækkefølge:', err);
      setSaveError('Rækkefølgen kunne ikke gemmes automatisk. Prøv igen.');
    } finally {
      setSaving(false);
    }
  };

  // Schedule auto-save
  const scheduleAutoSave = (newItems) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      persistOrder(newItems);
    }, 400);
  };

  // Move item by relative delta
  const moveItem = (index, delta) => {
    const targetIndex = index + delta;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const newItems = [...items];
    const [moved] = newItems.splice(index, 1);
    newItems.splice(targetIndex, 0, moved);
    setItems(newItems);
    scheduleAutoSave(newItems);
  };

  // Move item to absolute position (top or bottom)
  const moveToEdge = (index, position) => {
    if (index === position) return;
    const newItems = [...items];
    const [moved] = newItems.splice(index, 1);
    if (position === 'top') {
      newItems.unshift(moved);
    } else {
      newItems.push(moved);
    }
    setItems(newItems);
    scheduleAutoSave(newItems);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newItems = [...items];
    const [movedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, movedItem);

    setItems(newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
    scheduleAutoSave(newItems);
  };

  // Reset to default sorting
  const handleReset = async () => {
    if (!window.confirm('Er du sikker på, at du vil nulstille rækkefølgen til standard (efter upload-dato)?')) {
      return;
    }
    setSaving(true);
    try {
      await saveBeforeAfterOrder([]);
      clearCache('foer_efter');
      await loadData();
      const now = new Date();
      setLastSaved(now.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setShowSavedToast(true);
      setTimeout(() => setShowSavedToast(false), 3000);
    } catch (err) {
      setSaveError('Kunne ikke nulstille rækkefølge.');
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = searchQuery.trim()
    ? items.filter(
        item =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : items;

  return (
    <>
      <SEO
        title="Arranger Før & Efter Opslag - CMSNord"
        description="Skjult side til at tilpasse rækkefølgen af før- og efterprojekter."
        noIndex={true}
      />

      <div className="min-h-screen bg-zinc-950 text-zinc-100 py-10 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          {/* Breadcrumb / Back link */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <NavLink
              to="/galleri/foer-efter"
              className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-amber-400 hover:text-white transition-colors"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>Gå til den offentlige Før &amp; Efter side</span>
            </NavLink>

            <span className="inline-flex items-center gap-2 px-3 py-1 bg-zinc-900 border border-zinc-800 text-xs font-bold text-zinc-400 rounded">
              <i className="fa-solid fa-lock text-amber-400 text-xs" aria-hidden="true" />
              <span>Skjult side til sortering</span>
            </span>
          </div>

          {/* Header Card */}
          <div className="bg-zinc-900 border border-zinc-800 p-6 sm:p-8 rounded-lg mb-8 shadow-xl">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-400/10 border border-amber-400/30 text-amber-400 text-xs font-black uppercase tracking-wider mb-3">
                  <i className="fa-solid fa-arrows-up-down-left-right" aria-hidden="true" />
                  <span>Træk &amp; Slip Sortering</span>
                </div>
                <h1 className="text-2xl sm:text-4xl font-black uppercase tracking-tight text-white">
                  Sorter Før &amp; Efter Opslag
                </h1>
                <p className="text-zinc-400 text-sm sm:text-base mt-2 max-w-2xl">
                  Træk i håndtaget eller kortene for at ændre den rækkefølge, projekterne vises i på hjemmesiden.
                  Rækkefølgen <strong className="text-amber-400">gemmes automatisk</strong> i samme øjeblik, du slipper et opslag eller klikker på pilene.
                </p>
              </div>

              {/* Status and Actions */}
              <div className="flex flex-col sm:flex-row lg:flex-col items-start sm:items-center lg:items-end gap-3 shrink-0">
                {/* Auto-save Status Indicator */}
                <div className="flex items-center gap-2.5 px-4 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-xs font-bold">
                  {saving ? (
                    <>
                      <i className="fa-solid fa-circle-notch fa-spin text-amber-400" aria-hidden="true" />
                      <span className="text-amber-400">Gemmer automatisk...</span>
                    </>
                  ) : lastSaved ? (
                    <>
                      <i className="fa-solid fa-circle-check text-emerald-400" aria-hidden="true" />
                      <span className="text-emerald-400">Gemt (kl. {lastSaved})</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-cloud-arrow-up text-zinc-500" aria-hidden="true" />
                      <span className="text-zinc-400">Klar til sortering</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => persistOrder(items)}
                    disabled={saving || items.length === 0}
                    className="px-4 py-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-zinc-950 text-xs font-black uppercase tracking-wider transition-colors rounded flex items-center gap-2 cursor-pointer shadow-md"
                  >
                    <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
                    <span>Gem rækkefølge nu</span>
                  </button>

                  <a
                    href="/galleri/foer-efter"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-black uppercase tracking-wider transition-colors rounded flex items-center gap-1.5 cursor-pointer"
                    title="Se resultatet på den offentlige side"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                    <span>Se live</span>
                  </a>

                  <button
                    onClick={handleReset}
                    disabled={saving}
                    className="px-3 py-2 bg-zinc-900 hover:bg-red-950/60 text-zinc-400 hover:text-red-400 border border-zinc-800 hover:border-red-900/50 text-xs font-bold transition-colors rounded cursor-pointer"
                    title="Nulstil rækkefølge"
                  >
                    <i className="fa-solid fa-rotate-left mr-1" aria-hidden="true" />
                    Nulstil
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {saveError && (
              <div className="mt-4 p-3 bg-red-950/70 border border-red-800 text-red-200 text-xs rounded flex items-center gap-2">
                <i className="fa-solid fa-triangle-exclamation text-red-400 text-sm" aria-hidden="true" />
                <span>{saveError}</span>
              </div>
            )}
          </div>

          {/* Quick Search and Counters */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <div className="relative w-full sm:w-80">
              <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs" aria-hidden="true" />
              <input
                type="text"
                placeholder="Find projekt eller by i listen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-zinc-900 border border-zinc-800 focus:border-amber-400 rounded text-xs text-white placeholder-zinc-500 outline-none transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white text-xs cursor-pointer"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="text-xs text-zinc-400 font-bold flex items-center gap-3">
              <span>{items.length} projekter i alt</span>
              <span className="text-zinc-600">&bull;</span>
              <span className="text-amber-400">#1 vises øverst</span>
            </div>
          </div>

          {/* Project List */}
          {loading ? (
            <div className="p-16 text-center bg-zinc-900 border border-zinc-800 rounded-lg">
              <i className="fa-solid fa-circle-notch fa-spin text-3xl text-amber-400 mb-3" aria-hidden="true" />
              <p className="text-zinc-300 text-sm font-bold">Indlæser alle før- og efter-projekter...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center bg-zinc-900 border border-zinc-800 rounded-lg">
              <i className="fa-solid fa-images text-3xl text-zinc-600 mb-3" aria-hidden="true" />
              <p className="text-zinc-300 text-base font-bold">Ingen projekter fundet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item) => {
                const actualIndex = items.findIndex((i) => i.id === item.id);
                const isDragging = draggedIndex === actualIndex;
                const isDragOver = dragOverIndex === actualIndex;

                return (
                  <div
                    key={item.id}
                    draggable={!searchQuery}
                    onDragStart={(e) => handleDragStart(e, actualIndex)}
                    onDragOver={(e) => handleDragOver(e, actualIndex)}
                    onDrop={(e) => handleDrop(e, actualIndex)}
                    onDragEnd={handleDragEnd}
                    className={`relative bg-zinc-900 border rounded-lg transition-all select-none ${
                      isDragging
                        ? 'opacity-40 border-amber-400 scale-[0.99] shadow-2xl'
                        : isDragOver
                        ? 'border-amber-400 ring-2 ring-amber-400/40 bg-zinc-850'
                        : 'border-zinc-800 hover:border-zinc-700 shadow-md hover:shadow-lg'
                    }`}
                  >
                    {/* Visual drop indicator line */}
                    {isDragOver && (
                      <div className="absolute -top-1 left-0 right-0 h-1 bg-amber-400 rounded-full z-20 shadow-[0_0_10px_#F4AF00]" />
                    )}

                    <div className="p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                      
                      {/* Left: Drag Handle + Position Number + Info */}
                      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                        {/* Drag Handle */}
                        <div
                          className="shrink-0 w-8 h-12 sm:h-14 bg-zinc-950 border border-zinc-800 rounded flex items-center justify-center text-zinc-500 hover:text-amber-400 cursor-grab active:cursor-grabbing transition-colors"
                          title="Træk for at flytte"
                        >
                          <i className="fa-solid fa-grip-vertical text-base" aria-hidden="true" />
                        </div>

                        {/* Position Badge */}
                        <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-amber-400 text-zinc-950 font-black text-xs sm:text-sm flex items-center justify-center shadow-inner">
                          #{actualIndex + 1}
                        </div>

                        {/* Thumbnails (Før & Efter) */}
                        <div className="shrink-0 flex items-center gap-1.5 sm:gap-2">
                          {/* Før Billede */}
                          <div className="relative w-16 h-12 sm:w-20 sm:h-14 rounded overflow-hidden bg-zinc-950 border border-zinc-800 shrink-0">
                            {item.beforeUrl ? (
                              <img
                                src={`https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=75,format=auto/${item.beforeUrl.replace('https://assets.auxodev.dk/', '')}`}
                                alt={item.beforeCaption || item.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[10px] text-zinc-600">
                                Intet
                              </div>
                            )}
                            <span className="absolute bottom-0 left-0 right-0 bg-zinc-950/80 text-[9px] font-black uppercase text-center text-zinc-300 py-0.5">
                              Før
                            </span>
                          </div>

                          {/* Arrow connector */}
                          <i className="fa-solid fa-arrow-right text-[10px] text-zinc-600 shrink-0" aria-hidden="true" />

                          {/* Efter Billede */}
                          <div className="relative w-16 h-12 sm:w-20 sm:h-14 rounded overflow-hidden bg-zinc-950 border border-emerald-900/60 shrink-0">
                            {item.afterUrl ? (
                              <img
                                src={`https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=75,format=auto/${item.afterUrl.replace('https://assets.auxodev.dk/', '')}`}
                                alt={item.afterCaption || item.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[10px] text-zinc-600">
                                Intet
                              </div>
                            )}
                            <span className="absolute bottom-0 left-0 right-0 bg-emerald-950/90 text-[9px] font-black uppercase text-center text-emerald-300 py-0.5">
                              Efter
                            </span>
                          </div>
                        </div>

                        {/* Title and details */}
                        <div className="min-w-0 flex-1 pl-1">
                          <h3 className="text-sm sm:text-base font-black uppercase tracking-tight text-white truncate">
                            {item.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-zinc-400">
                            {item.location && (
                              <span className="inline-flex items-center gap-1 text-zinc-300">
                                <i className="fa-solid fa-location-dot text-amber-400 text-[10px]" aria-hidden="true" />
                                {item.location}
                              </span>
                            )}
                            {item.category && (
                              <span className="px-1.5 py-0.5 bg-zinc-800 text-zinc-400 text-[10px] font-bold uppercase rounded">
                                {item.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Step Move Buttons for Mobile/Keyboard/Quick clicks */}
                      <div className="flex items-center justify-end gap-1.5 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-zinc-800/80">
                        {/* Move to Top */}
                        <button
                          onClick={() => moveToEdge(actualIndex, 'top')}
                          disabled={actualIndex === 0}
                          className="w-8 h-8 rounded bg-zinc-950 hover:bg-zinc-800 disabled:opacity-30 disabled:hover:bg-zinc-950 text-zinc-400 hover:text-amber-400 border border-zinc-800 transition-colors flex items-center justify-center text-xs cursor-pointer"
                          title="Flyt helt til toppen (#1)"
                        >
                          <i className="fa-solid fa-angles-up" aria-hidden="true" />
                        </button>

                        {/* Move Up */}
                        <button
                          onClick={() => moveItem(actualIndex, -1)}
                          disabled={actualIndex === 0}
                          className="w-8 h-8 rounded bg-zinc-950 hover:bg-zinc-800 disabled:opacity-30 disabled:hover:bg-zinc-950 text-zinc-400 hover:text-white border border-zinc-800 transition-colors flex items-center justify-center text-xs cursor-pointer"
                          title="Flyt 1 plads op"
                        >
                          <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                        </button>

                        {/* Move Down */}
                        <button
                          onClick={() => moveItem(actualIndex, 1)}
                          disabled={actualIndex === items.length - 1}
                          className="w-8 h-8 rounded bg-zinc-950 hover:bg-zinc-800 disabled:opacity-30 disabled:hover:bg-zinc-950 text-zinc-400 hover:text-white border border-zinc-800 transition-colors flex items-center justify-center text-xs cursor-pointer"
                          title="Flyt 1 plads ned"
                        >
                          <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                        </button>

                        {/* Move to Bottom */}
                        <button
                          onClick={() => moveToEdge(actualIndex, 'bottom')}
                          disabled={actualIndex === items.length - 1}
                          className="w-8 h-8 rounded bg-zinc-950 hover:bg-zinc-800 disabled:opacity-30 disabled:hover:bg-zinc-950 text-zinc-400 hover:text-amber-400 border border-zinc-800 transition-colors flex items-center justify-center text-xs cursor-pointer"
                          title="Flyt helt til bunden"
                        >
                          <i className="fa-solid fa-angles-down" aria-hidden="true" />
                        </button>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer helper info */}
          <div className="mt-8 p-4 bg-zinc-900/60 border border-zinc-800 rounded text-xs text-zinc-400 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-info-circle text-amber-400" aria-hidden="true" />
              <span>Tip: Du kan bruge pilene til at finjustere, eller trække kortene direkte for større spring.</span>
            </div>
            <a
              href="/galleri/foer-efter"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-400 hover:underline font-bold"
            >
              Åbn /galleri/foer-efter &rarr;
            </a>
          </div>

        </div>
      </div>

      {/* Floating Save Confirmation Toast */}
      {showSavedToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-3 rounded-lg shadow-2xl border border-emerald-400 flex items-center gap-3 animate-bounce">
          <i className="fa-solid fa-circle-check text-lg" aria-hidden="true" />
          <div className="text-xs">
            <p className="font-black uppercase tracking-wider">Rækkefølgen er gemt!</p>
            <p className="opacity-90">Den nye rækkefølge er nu aktiv på før/efter-siden.</p>
          </div>
        </div>
      )}
    </>
  );
}
